import type {
  AccountProfile,
  PlayerDevelopmentResponse,
  PlayerDevelopmentScope,
  PlayerProfile,
  UpdateAccountProfileRequest,
} from "@chessla/shared";
import { apiClient } from "@/shared/api/client";

export const fetchMyPlayerProfile = (): Promise<PlayerProfile> => apiClient<PlayerProfile>("/players/me");

export interface UpdatePlayerFields {
  goals?: string[];
  weaknesses?: string[];
  strengths?: string[];
}

/** Updates the caller's own mutable profile fields (goals, strengths, weaknesses). The player is always the one editing these — Nikola has no tool to write them. */
export const updateMyPlayerProfile = (fields: UpdatePlayerFields): Promise<PlayerProfile> =>
  apiClient<PlayerProfile>("/players/me", {
    method: "PUT",
    body: JSON.stringify(fields),
  });

export const updateMyAccountProfile = (fields: UpdateAccountProfileRequest): Promise<AccountProfile> =>
  apiClient<AccountProfile>("/auth/me", {
    method: "PUT",
    body: JSON.stringify(fields),
  });

export const relinkMyChessAccount = (username: string): Promise<PlayerProfile> =>
  apiClient<PlayerProfile>("/chess-com/account", {
    method: "PUT",
    body: JSON.stringify({ username }),
  });

export interface PlayerStats {
  totalGames: number;
  analyzedGames: number;
  wins: number;
  losses: number;
  draws: number;
  winRate: number;
  avgAccuracy: number | null;
  avgCpl: number | null;
  avgEstimatedRating: number | null;
  estimatedRatingGames: number;
  estimatedRatingConfidence: "none" | "low" | "medium" | "high";
  cleanGames: number;
  bestMoves: number;
  brilliantMoves: number;
  excellentMoves: number;
  goodMoves: number;
  bookMoves: number;
  blunders: number;
  mistakes: number;
  imprecisions: number;
  bestGameAccuracy: number | null;
  worstGameAccuracy: number | null;
}

export const fetchMyPlayerStats = (): Promise<PlayerStats> => apiClient<PlayerStats>("/players/me/stats");

export interface PlayerAnalytics {
  overview: {
    ratedGames: number;
    standardGames: number;
    uniqueOpponents: number;
    averageGameDurationSeconds: number | null;
    ratingVolatility: number | null;
    conversionRate: number | null;
    saveRate: number | null;
  };
  timeManagement: {
    movesWithClock: number;
    averageThinkSeconds: number | null;
    averageErrorThinkSeconds: number | null;
    rushedMoves: number;
    rushedErrorRate: number | null;
    longThinkMoves: number;
    longThinkErrorRate: number | null;
    timePressureMoves: number;
    timePressureErrorRate: number | null;
  };
  sessions: {
    count: number;
    averageGames: number;
    tiltSessions: number;
    lateSessionAccuracyDelta: number | null;
  };
  colors: Array<{ color: "w" | "b"; games: number; scoreRate: number; accuracy: number | null }>;
  opponentBands: Array<{ band: string; games: number; scoreRate: number }>;
  openings: Array<{
    opening: string;
    tutorialUrl: string | null;
    games: number;
    scoreRate: number;
    accuracy: number | null;
    averageCpl: number | null;
  }>;
  defenses: Array<{
    opening: string;
    tutorialUrl: string | null;
    games: number;
    scoreRate: number;
    accuracy: number | null;
    averageCpl: number | null;
  }>;
  terminations: Array<{ reason: string; games: number }>;
  trends: Array<{ month: string; games: number; scoreRate: number; accuracy: number | null; rating: number | null }>;
  byHour: Array<{ hour: number; games: number; scoreRate: number; accuracy: number | null }>;
  byWeekday: Array<{ weekday: number; games: number; scoreRate: number; accuracy: number | null }>;
}

export const fetchMyPlayerAnalytics = (): Promise<PlayerAnalytics> =>
  apiClient<PlayerAnalytics>("/players/me/analytics");

export const playerDevelopmentUrl = ({
  timeFormat,
  exactTimeControl,
  windowDays,
  from,
  toExclusive,
}: PlayerDevelopmentScope): string => {
  const query = new URLSearchParams({ timeFormat });
  if (exactTimeControl != null) query.set("exactTimeControl", exactTimeControl);
  if (windowDays) query.set("windowDays", String(windowDays));
  if (from) query.set("from", from);
  if (toExclusive) query.set("toExclusive", toExclusive);
  return `/players/me/development?${query}`;
};

export const fetchMyPlayerDevelopment = (
  scope: PlayerDevelopmentScope,
  signal?: AbortSignal,
): Promise<PlayerDevelopmentResponse> => apiClient<PlayerDevelopmentResponse>(playerDevelopmentUrl(scope), { signal });

export const completeOnboarding = (): Promise<{ onboardingCompleted: true }> =>
  apiClient<{ onboardingCompleted: true }>("/players/me/onboarding/complete", {
    method: "POST",
  });

export const deleteMyAccount = (): Promise<{ deleted: true }> =>
  apiClient<{ deleted: true }>("/auth/account", { method: "DELETE" });

export type InsightField = "strengths" | "weaknesses" | "goals";

export interface GenerateInsightsResult {
  suggestions: string[];
  creditsCharged: number;
}

export const generateInsights = (field: InsightField): Promise<GenerateInsightsResult> =>
  apiClient<GenerateInsightsResult>("/coach/generate-insights", {
    method: "POST",
    body: JSON.stringify({ field, requestId: crypto.randomUUID() }),
  });
